import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Users, Plus, Loader2 } from 'lucide-react';
import { UserProfile } from '@/types/user';
import { projectAPI } from '@/utils/supabase/client';
import { PendingUsersSection } from './PendingUsersSection';
import { InvitedUsersSection } from './InvitedUsersSection';
import { UserList } from './UserList';
import { InviteUserDialog } from './InviteUserDialog';
import { UserDetailSheet } from './UserDetailSheet';

export function TeamPage() {
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState<UserProfile | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const [isInviteOpen, setIsInviteOpen] = useState(false);

  const loadUsers = async () => {
    try {
      setLoading(true);
      const data = await projectAPI.getUsers();
      setUsers(data);
    } catch (error) {
      console.error('Failed to load users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadUsers();
  }, []);

  const handleUserClick = (user: UserProfile) => {
    setSelectedUser(user);
    setIsSheetOpen(true);
  };

  const handleUserUpdated = () => {
    loadUsers();
  };

  const handleSheetClose = () => {
    setIsSheetOpen(false);
    setSelectedUser(null);
    handleUserUpdated();
  };

  const handleInviteSuccess = () => {
    setIsInviteOpen(false);
    handleUserUpdated();
  };

  // Separate users by status
  // Pending: self-signups waiting for role assignment
  const pendingUsers = users.filter(u => u.role === 'pending' && u.status === 'active');
  // Invited: users who were invited but haven't accepted yet (no auth_user_id linked)
  const invitedUsers = users.filter(u => u.status === 'invited' && !u.auth_user_id);
  // Active: all users with a role (not pending) who aren't deactivated
  // Includes: seeded users, users who accepted invites (have auth_user_id even if status='invited')
  const activeUsers = users.filter(u =>
    u.role !== 'pending' &&
    u.status !== 'deactivated' &&
    // Show if NOT invited OR if they've accepted their invite (has auth_user_id)
    (u.status !== 'invited' || u.auth_user_id)
  );

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold font-serif tracking-tight flex items-center gap-2">
            <Users className="h-6 w-6" />
            Team Management
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage user access and permissions
          </p>
        </div>
        <Button onClick={() => setIsInviteOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Invite User
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
        </div>
      ) : (
        <>
          {/* Pending Users Section */}
          {pendingUsers.length > 0 && (
            <PendingUsersSection
              users={pendingUsers}
              onUserClick={handleUserClick}
              onUserUpdated={handleUserUpdated}
            />
          )}

          {/* Invited Users Section */}
          {invitedUsers.length > 0 && (
            <InvitedUsersSection
              users={invitedUsers}
              onResendInvite={async (userId) => {
                await projectAPI.resendInvite(userId);
                handleUserUpdated();
              }}
              onCancelInvite={async (userId) => {
                await projectAPI.cancelInvite(userId);
                handleUserUpdated();
              }}
            />
          )}

          {/* Active Users List */}
          <UserList
            users={activeUsers}
            onUserClick={handleUserClick}
          />
        </>
      )}

      {/* User Detail Sheet */}
      <UserDetailSheet
        user={selectedUser}
        open={isSheetOpen}
        onOpenChange={(open) => {
          if (!open) handleSheetClose();
        }}
        onUpdate={handleUserUpdated}
      />

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={isInviteOpen}
        onOpenChange={setIsInviteOpen}
        onSuccess={handleInviteSuccess}
      />
    </div>
  );
}
